import { Spinner } from "@/components/ui/Spinner";
import { Surface } from "@/components/ui/Surface";
import { Text } from "@/components/ui/Typography";
import { useModalLayer } from "@/contexts/ModalContext";
import { DURATION, TRANSITION } from "@/lib/motion";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { FeedbackIcon } from "./FeedbackIcon";
import { OVERLAY_BACKDROP, OVERLAY_CARD, overlayCardMotion } from "./overlay-styles";

const MotionSurface = motion.create(Surface);

export type StatusOverlayStatus = "loading" | "success" | "error";

type StatusOverlayProps = {
  open: boolean;
  status: StatusOverlayStatus;
  title: string;
  message?: ReactNode;
  /** Actions rendered below the text (e.g. "Acompanhar pedido" link) */
  children?: ReactNode;
};

/**
 * Blocking full-screen status overlay for long-running flows (checkout,
 * cart flush). Transitions between loading/success/error are animated.
 */
export function StatusOverlay({ open, status, title, message, children }: StatusOverlayProps) {
  const reduceMotion = useReducedMotion();
  const { zIndex } = useModalLayer(open);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="alertdialog"
          aria-modal="true"
          aria-busy={status === "loading"}
          aria-live="assertive"
          style={{ zIndex }}
          className={OVERLAY_BACKDROP}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION.moderate }}
        >
          <MotionSurface surface="card" className={OVERLAY_CARD} {...overlayCardMotion(reduceMotion)}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={status}
                className="flex w-full flex-col items-center gap-4"
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                transition={TRANSITION.fade}
              >
                {status === "loading" ? <Spinner size="lg" tone="primary" /> : <FeedbackIcon variant={status} />}
                <div className="flex flex-col gap-2">
                  <Text size="xl" weight="bold" tone="default">
                    {title}
                  </Text>
                  {message && (
                    <Text as="div" leading="relaxed" tone="muted">
                      {message}
                    </Text>
                  )}
                </div>
                {children}
              </motion.div>
            </AnimatePresence>
          </MotionSurface>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
